import React from 'react'
import { PlayIcon } from '@heroicons/react/24/outline'
import { ChallengeService, TaskService } from '@duck/api-definitions'
import { MarkdownEditor } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules'
import { CoverArt } from './CoverArt'
import { WeaponIcon } from '@components/WeaponIcon'
import { TASK_PROFICIENCIES } from '@/utils/taskProficiencies'
import { challengeDifficultyDisplayMap, getChallengeDifficultyColor, timeCategoryDisplayMap } from '@/utils/challengeData.utils'
import { useChallengeAction } from './useChallengeAction'

const WEAPON_LABEL_KEY: Record<ChallengeService.TaskWeaponsOfChoiceEnum, TranslationKey> = {
  [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: 'common.sql',
  [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: 'common.python',
}

interface ChallengeDetailProps {
  challenge: ChallengeService.AvailableChallenge
  onStart: () => void
  /** Override the cover height/sizing (e.g. shorter on small screens). */
  coverClassName?: string
}

export const ChallengeDetail = ({ challenge, onStart, coverClassName = 'h-44 w-full flex-none' }: ChallengeDetailProps) => {
  const { t } = useTar()
  const action = useChallengeAction(challenge)
  return (
    <div className="flex h-full flex-col">
      <CoverArt taskId={challenge.taskId} coverImageUrl={challenge.coverImageUrl} focusY={challenge.coverFocusY} alt={challenge.name} className={coverClassName} />
      <div className="flex-1 overflow-y-auto p-6">
        <h2 className="mb-4 text-xl font-bold text-foreground">{challenge.name}</h2>
        <dl className="mb-6 flex flex-col gap-3 text-sm">
          <div className="flex items-center gap-2">
            <dt className="w-32 flex-none font-medium text-muted-foreground">{t('challenges.difficultyLabel')}</dt>
            <dd>
              <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${getChallengeDifficultyColor(challenge.difficulty)}`}>
                {t(challengeDifficultyDisplayMap[challenge.difficulty])}
              </span>
            </dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="w-32 flex-none font-medium text-muted-foreground">{t('challenges.availableInLabel')}</dt>
            <dd className="flex flex-wrap items-center gap-3">
              {challenge.allowedWeapons.map((w) => (
                <span key={w} className="inline-flex items-center gap-1.5 text-foreground">
                  <WeaponIcon weapon={w} className="h-4 w-4 flex-none" />
                  {t(WEAPON_LABEL_KEY[w])}
                </span>
              ))}
            </dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="w-32 flex-none font-medium text-muted-foreground">{t('challenges.timeEstimationLabel')}</dt>
            <dd className="text-foreground">{t(timeCategoryDisplayMap[challenge.timeCategory])}</dd>
          </div>
        </dl>
        {challenge.description !== undefined && challenge.description !== '' ? (
          // `MarkdownEditor` is uncontrolled (defaultValue applies on mount only),
          // so key it on the challenge identity to remount on navigation — otherwise
          // the first opened description sticks for every subsequent challenge.
          // Borderless, full-bleed, transparent surface so the copy reads as a
          // plain paragraph on the card rather than a boxed-in field.
          <MarkdownEditor key={challenge.challengeId ?? challenge.taskId} defaultValue={challenge.description} mode="view" noBorder className="h-auto" mdClassName="bg-transparent p-0" />
        ) : null}
        {challenge.proficiencies !== undefined && challenge.proficiencies.length > 0 ? (
          <div className="mt-6">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('challenges.skillsYouWillPractice')}</h3>
            <div className="flex flex-wrap gap-3">
              {challenge.proficiencies.map((p) => {
                const data = TASK_PROFICIENCIES[p as TaskService.TaskProficienciesEnum]
                return (
                  <span key={p} className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm">
                    {data?.icon('h-5 w-5')}<span className="capitalize">{t(data?.label)}</span>
                  </span>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
      <div className="flex flex-none gap-3 border-t border-border p-5">
        <button
          type="button"
          onClick={onStart}
          disabled={action.disabled}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 font-bold text-white disabled:cursor-not-allowed disabled:opacity-60 ${action.className}`}
        >
          {action.showsCooldownTime ? (
            <span>{action.label}</span>
          ) : (
            <>
              <PlayIcon className="w-4 h-4" />
              <span>{action.label}</span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
